import { z } from 'zod';
import { SKIP_REASONS } from '../campaigns/eligibility.js';
import { MAX_FREQUENCY_DAYS } from '../campaigns/plan.js';
import { TEMPLATE_MAX_CHARS } from '../campaigns/template.js';

/**
 * Campaigns through the worker API (M9). Admins only; the worker re-checks the role in the database.
 * Drafts, tests, scheduling, launch, pause, resume and cancel are worker calls; lists and details are
 * read through RLS (admins only).
 */

export const FIELD_CONDITION_OPS = [
  'is',
  'is_not',
  'contains',
  'empty',
  'not_empty',
  'gt',
  'lt',
  'before',
  'after',
] as const;
export type FieldConditionOp = (typeof FIELD_CONDITION_OPS)[number];

export const FIELD_CONDITION_LABELS: Record<FieldConditionOp, string> = {
  is: 'is',
  is_not: 'is not',
  contains: 'contains',
  empty: 'is empty',
  not_empty: 'is not empty',
  gt: 'is more than',
  lt: 'is less than',
  before: 'is before',
  after: 'is after',
};

/** Which conditions make sense for each field type. */
export const OPS_BY_FIELD_TYPE: Record<string, readonly FieldConditionOp[]> = {
  text: ['is', 'is_not', 'contains', 'empty', 'not_empty'],
  dropdown: ['is', 'is_not', 'empty', 'not_empty'],
  number: ['is', 'gt', 'lt', 'empty', 'not_empty'],
  currency: ['is', 'gt', 'lt', 'empty', 'not_empty'],
  date: ['before', 'after', 'empty', 'not_empty'],
  boolean: ['is', 'empty', 'not_empty'],
};

export const FieldConditionSchema = z.object({
  fieldId: z.uuid(),
  op: z.enum(FIELD_CONDITION_OPS),
  /** Text, dropdown and date: a string (date YYYY-MM-DD); number: a number; currency: whole cents. */
  value: z.union([z.string().trim().max(200), z.number().finite(), z.boolean(), z.null()]),
});
export type FieldCondition = z.infer<typeof FieldConditionSchema>;

const Uuids = (max: number) => z.array(z.uuid()).max(max).default([]);

export const AudienceSchema = z.object({
  /** Every merchant in the workspace (the other filters still narrow it). */
  everyone: z.boolean().default(false),
  tagsAny: Uuids(50),
  tagsAll: Uuids(50),
  excludeTags: Uuids(50),
  sources: z.array(z.string().trim().min(1).max(200)).max(50).default([]),
  importIds: Uuids(50),
  repIds: Uuids(100),
  states: z
    .array(z.string().regex(/^[A-Z]{2}$/))
    .max(60)
    .default([]),
  fields: z.array(FieldConditionSchema).max(20).default([]),
});
export type Audience = z.infer<typeof AudienceSchema>;

export const EMPTY_AUDIENCE: Audience = {
  everyone: false,
  tagsAny: [],
  tagsAll: [],
  excludeTags: [],
  sources: [],
  importIds: [],
  repIds: [],
  states: [],
  fields: [],
};

/** An audience must say who it includes; "exclude" filters alone do not choose anyone. */
export function hasAudienceCriteria(a: Audience): boolean {
  return (
    a.everyone ||
    a.tagsAny.length > 0 ||
    a.tagsAll.length > 0 ||
    a.sources.length > 0 ||
    a.importIds.length > 0 ||
    a.repIds.length > 0 ||
    a.states.length > 0 ||
    a.fields.length > 0
  );
}

/** Reads `campaigns.audience` (jsonb). Invalid stored data reads as an empty audience (chooses no one). */
export function readAudience(raw: unknown): Audience {
  const parsed = AudienceSchema.safeParse(raw);
  return parsed.success ? parsed.data : EMPTY_AUDIENCE;
}

export const AI_MODELS = ['haiku', 'sonnet'] as const;

export const CampaignDraftSchema = z.object({
  name: z.string().trim().min(1, 'Name the campaign').max(80, 'Use at most 80 characters'),
  audience: AudienceSchema,
  template: z.string().max(TEMPLATE_MAX_CHARS + 200),
  /** More than 1 needs the admin's confirmation at launch, recorded in audit_log (CLAUDE.md §1.4). */
  maxSegments: z.number().int().min(1).max(10),
  frequencyDays: z.number().int().min(0).max(MAX_FREQUENCY_DAYS),
  /** Stored now; AI replies arrive in M11. */
  aiEnabled: z.boolean(),
  aiModel: z.enum(AI_MODELS),
});
export type CampaignDraft = z.infer<typeof CampaignDraftSchema>;

export const CreateCampaignResponseSchema = z.object({ campaignId: z.uuid() });

export const SkipCountsSchema = z.object(
  Object.fromEntries(SKIP_REASONS.map((r) => [r, z.number().int()])) as Record<
    (typeof SKIP_REASONS)[number],
    z.ZodNumber
  >,
);

export const CampaignSampleSchema = z.object({
  merchantId: z.uuid(),
  label: z.string(),
  text: z.string(),
  segments: z.number().int(),
  encoding: z.enum(['gsm7', 'ucs2']),
});

export const CampaignPreviewSchema = z.object({
  /** Merchants the filters match. */
  matched: z.number().int(),
  /** Of those, who would get a text. */
  eligible: z.number().int(),
  skipped: SkipCountsSchema,
  segmentsTotal: z.number().int(),
  maxSegmentsSeen: z.number().int(),
  /** Some rendered text needs Unicode (UCS-2). */
  usesUnicode: z.boolean(),
  estimateCents: z.string(),
  spendLimitCents: z.string(),
  sendingDays: z.number().int().nullable(),
  /** The earliest an eligible merchant's sending window opens (ISO), or null. */
  firstSendAt: z.iso.datetime({ offset: true }).nullable(),
  samples: z.array(CampaignSampleSchema),
  /** Problems that stop scheduling or launching, in words. Empty = it can go. */
  blockers: z.array(z.string()),
  /** Template problems (also among the blockers), for the editor. */
  templateIssues: z.array(z.object({ code: z.string(), message: z.string() })),
});
export type CampaignPreview = z.infer<typeof CampaignPreviewSchema>;

export const LaunchCampaignRequestSchema = z.object({
  /** One per click of the launch dialog: a retried request finds the campaign already launched. */
  launchKey: z.uuid(),
  /** Required when the campaign allows more than one segment (#14). */
  confirmMultiSegment: z.boolean().default(false),
});
export type LaunchCampaignRequest = z.infer<typeof LaunchCampaignRequestSchema>;

export const ScheduleCampaignRequestSchema = z.object({
  at: z.iso.datetime({ offset: true }),
  confirmMultiSegment: z.boolean().default(false),
});
export type ScheduleCampaignRequest = z.infer<typeof ScheduleCampaignRequestSchema>;

export const CampaignStatusResponseSchema = z.object({
  campaignId: z.uuid(),
  status: z.enum(['draft', 'scheduled', 'running', 'paused', 'completed', 'cancelled']),
});
export type CampaignStatusResponse = z.infer<typeof CampaignStatusResponseSchema>;

export const TestSendRequestSchema = z.object({
  merchantId: z.uuid(),
  /** Client-generated per click, so a double click sends one test. */
  clientKey: z.uuid(),
});
export type TestSendRequest = z.infer<typeof TestSendRequestSchema>;

export const TestSendResponseSchema = z.object({ messageId: z.uuid() });

export const PauseAllResponseSchema = z.object({ paused: z.number().int() });

export const CampaignStatsSchema = z.object({
  recipients: z.number().int(),
  pending: z.number().int(),
  skipped: SkipCountsSchema,
  queued: z.number().int(),
  held: z.number().int(),
  sending: z.number().int(),
  sent: z.number().int(),
  delivered: z.number().int(),
  failed: z.number().int(),
  unknown: z.number().int(),
  blocked: z.number().int(),
  cancelled: z.number().int(),
  expired: z.number().int(),
  replied: z.number().int(),
  optedOut: z.number().int(),
  segments: z.number().int(),
});
export type CampaignStats = z.infer<typeof CampaignStatsSchema>;
